/* eslint-disable no-throw-literal */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { orderBy } from 'lodash';
import { useHistory } from 'react-router-dom';

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy
} from '@dnd-kit/sortable';

import {
  LostLeadsPageActions,
  PipelineStage,
  PipelineStageForm,
  SalesFiltersModal,
} from '../../../components/sales';
import ConfirmationModal from '../../../components/shared/modal/ConfirmationModal';
import LoadScreen from '../../../components/load-screen';
import Button from '../../../components/shared/Button';
import LeadCard from '../../../components/sales/LeadCard';

import {
  getPipelineStages,
  updatePipelineStage,
  createPipelineStage,
  deletePipelineStage,
  getPipelineLabels,
  getPipelineById,
} from '../../../actions/salesActions';
import {
  getLeadsWithActivity,
  setLeads,
  bulkUpdateLeads,
  exportLeads,
} from '../../../actions/leadsActions';
import { getSurgeons } from '../../../actions/surgeonsActions';
import { getProcedures } from '../../../actions/proceduresActions';

import { useAlert } from '../../../hooks';

import routes from '../../../constants/routes';
import { LOST_LEADS_PIPELINE_ID } from '../../../constants/sales';

import './sales-page.scss';

const SalesLeadsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { showAlert } = useAlert();

  const currentUser = useSelector((state) => state.user.currentUser);
  const currentTenant = useSelector((state) => state.tenant.currentTenant);
  const leads = useSelector((state) => state.leads?.list?.filter((l) => !!l.active) || []);
  const surgeons = useSelector((state) => state.surgeons.list);
  const labels = useSelector((state) => state.pipelines?.labels || []);
  // const pipelines = useSelector((state) => state.pipelines?.list || []);
  // const procedures = useSelector((state) => state.procedures.list);
  const users = useSelector((state) => state.users.list);

  const [fetching, setFetching] = useState(false);

  const [editMode, toggleEditMode] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [activeLeadId, setActiveLeadId] = useState(null);

  const [deleteStageModalOpen, toggleDeleteStageModal] = useState(false);
  const [filtersModalOpen, toggleFiltersModal] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteStageLoading, setDeleteStageLoading] = useState(false);
  const [exportLoading, toggleExportLoading] = useState(false);

  const [pipelineStages, setPipelineStages] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [filters, setFilters] = useState({
    owners: [],
    surgeons: [],
    labels: [],
  });

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const scrollContainerRef = useRef();

  const updateShadows = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftShadow(container.scrollLeft > 0);
    setShowRightShadow(container.scrollLeft < container.scrollWidth - container.clientWidth);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', updateShadows);

    return () => {
      container.removeEventListener('scroll', updateShadows);
      window.removeEventListener('resize', updateShadows);
    };
  }, []);

  useEffect(() => {
    onLoad().catch();
    // const unsubscribe = dispatch(subscribeToPipelines());
    //
    // return () => {
    //   unsubscribe();
    // };
  }, []);

  const onLoad = async () => {
    try {
      setFetching(true);
      const res = await dispatch(getPipelineById(LOST_LEADS_PIPELINE_ID));

      if (res) {
        setSelectedPipeline(res.id);
        await fetchPipelineStages(LOST_LEADS_PIPELINE_ID);
      }

      await Promise.all([
        dispatch(getPipelineLabels()),
        dispatch(getSurgeons()),
        dispatch(getProcedures()),
        dispatch(getLeadsWithActivity(LOST_LEADS_PIPELINE_ID))
      ]);
      updateShadows();
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const fetchPipelineStages = async (pipelineId) => {
    try {
      const res = await dispatch(getPipelineStages(pipelineId));
      if (res) {
        setPipelineStages(res || []);
        setAllStages([...allStages.filter((s) => s.pipelineId !== pipelineId), ...res]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onAddStageClick = () => {
    const arr = [...pipelineStages];
    arr.push({
      tempId: uuid().substring(0, 20),
      title: '',
      description: '',
      probability: 100,
      rottingPeriod: 100,
      orderNumber: arr.length + 1,
    });
    setPipelineStages(arr);
  };

  const onSaveClick = async () => {
    try {
      setSaveLoading(true);

      for (const stage of pipelineStages) {
        const data = {
          title: stage.title || '',
          description: stage.description || '',
          probability: Number(stage.probability) || 0,
          rottingPeriod: Number(stage.rottingPeriod) || 0,
          orderNumber: stage.orderNumber,
        };
        if (stage.id) {
          await dispatch(updatePipelineStage(selectedPipeline, stage.id, data));
        } else {
          await dispatch(createPipelineStage(selectedPipeline, data));
        }
      }

      await fetchPipelineStages(selectedPipeline);
      toggleEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaveLoading(false);
    }
  };

  const onDeleteStage = async () => {
    try {
      if (selectedStage) {
        setDeleteStageLoading(true);

        if (selectedStage.id) {
          await dispatch(deletePipelineStage(selectedPipeline, selectedStage.id));
          setPipelineStages([...pipelineStages].filter((s) => s.id !== selectedStage.id));
          await fetchPipelineStages(selectedPipeline);
        } else {
          setPipelineStages([...pipelineStages].filter((s) => s.tempId !== selectedStage.tempId));
        }

        toggleDeleteStageModal(false);
        setSelectedStage(null)
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteStageLoading(false);
    }
  };

  const onStageChange = (stageId, field, value) => {
    const arr = [...pipelineStages];
    const stage = arr.find((s) => s.id === stageId || s.tempId === stageId);

    stage[field] = value;
    setPipelineStages(arr);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = pipelineStages.findIndex(
        (s) => (s.id || s.tempId) === active.id
      );
      const newIndex = pipelineStages.findIndex(
        (s) => (s.id || s.tempId) === over?.id
      );

      const newStages = arrayMove(pipelineStages, oldIndex, newIndex).map(
        (stage, index) => ({
          ...stage,
          orderNumber: index + 1,
        })
      );
      setPipelineStages(newStages);
    }
  };

  const handleLeadDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const draggedLead = leads.find((l) => l.id === active.id);
    const overLead = leads.find((l) => l.id === over.id);
    const destinationStageId = overLead?.stage || over?.id;
    const sourceStageId = draggedLead?.stage;

    if (!draggedLead || !destinationStageId) {
      return;
    }

    // Same-stage reordering
    if (sourceStageId === destinationStageId) {
      const stageLeads = orderBy(leads.filter((l) => l.stage === sourceStageId), 'orderNumber');
      const oldIndex = stageLeads.findIndex((l) => l.id === active.id);
      const newIndex = stageLeads.findIndex((l) => l.id === over?.id);

      const newLeads = arrayMove(stageLeads, oldIndex, newIndex).map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const arr = [...leads];
      newLeads.forEach((lead) => {
        const updatedLead = arr.find((l) => l.id === lead.id);
        if (updatedLead) {
          updatedLead.orderNumber = lead.orderNumber;
        }
      });

      dispatch(setLeads(arr));
      dispatch(bulkUpdateLeads(newLeads));
    }

    // Cross-stage move
    if (sourceStageId !== destinationStageId) {
      const sourceLeads = orderBy(
        leads.filter((l) => l.stage === sourceStageId && l.id !== active.id),
        'orderNumber'
      );

      const destinationLeads = orderBy(
        leads.filter((l) => l.stage === destinationStageId),
        'orderNumber'
      );

      const insertIndex = overLead
        ? destinationLeads.findIndex((l) => l.id === overLead.id)
        : destinationLeads.length;

      const newDraggedLead = {
        ...draggedLead,
        stage: destinationStageId,
      };

      destinationLeads.splice(insertIndex, 0, newDraggedLead);

      const updatedSourceLeads = sourceLeads.map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const updatedDestinationLeads = destinationLeads.map((lead, index) => ({
        ...lead,
        orderNumber: index + 1,
      }));

      const updatedLeads = [...updatedSourceLeads, ...updatedDestinationLeads];

      const arr = [...leads];
      updatedLeads.forEach((lead) => {
        const updatedLead = arr.find((l) => l.id === lead.id);
        if (updatedLead) {
          updatedLead.stage = lead.stage;
          updatedLead.orderNumber = lead.orderNumber;
        }
      });

      dispatch(setLeads(arr));
      dispatch(bulkUpdateLeads(updatedLeads));
    }
  };

  const onLeadClick = (lead) => {
    history.push(`${routes.LEADS}/${lead.id}`);

    // setSelectedLead(lead);
    // toggleLeadModal(true);
  }

  const onExportClick = async () => {
    toggleExportLoading(true);

    try {
      await dispatch(exportLeads());
    } catch (err) {
      console.error(err);
    } finally {
      toggleExportLoading(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    })
  );

  const pipelineLeads = useMemo(() => leads.filter((l) => l.pipeline === selectedPipeline), [leads, selectedPipeline]);

  const filteredLeads = useMemo(() => (
    pipelineLeads.filter((lead) => {
      const ownerFilter = !filters.owners?.length || filters.owners.includes(lead.owner);
      const surgeonFilter = !filters.surgeons?.length || filters.surgeons.includes(lead.surgeon);
      const labelFilter = !filters.labels?.length || lead.labels?.some((labelId) => filters.labels.includes(labelId));

      return ownerFilter && surgeonFilter && labelFilter;
    })
  ), [pipelineLeads, filters]);

  const activeFilterCount = useMemo(() => (
    (filters.owners?.length || 0) + (filters.surgeons?.length || 0) + (filters.labels?.length || 0)
  ), [filters]);

  const clearFilters = () => {
    setFilters({
      owners: [],
      surgeons: [],
      labels: [],
    });
  };

  return (
    <div className='sales-page__container'>
      {fetching && <LoadScreen />}

      <LostLeadsPageActions
        userRole={currentUser.role}
        stages={pipelineStages}
        leads={filteredLeads}
        selectedPipeline={selectedPipeline}
        loading={saveLoading}
        editMode={editMode}
        onEditClick={() => toggleEditMode(true)}
        onSaveChangesClick={onSaveClick}
        onCancelClick={() => toggleEditMode(false)}
        onExportClick={onExportClick}
        exportLoading={exportLoading}
        onFilterClick={() => toggleFiltersModal(true)}
        activeFilterCount={activeFilterCount}
      />

      <div
        className={`scroll-shadow-wrapper ${showLeftShadow ? 'shadow-left' : ''} ${showRightShadow ? 'shadow-right' : ''}`}
      >
        <div className='sales-page__stage-items-list' ref={scrollContainerRef}>
          {editMode ? (
            <DndContext
              collisionDetection={closestCenter}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pipelineStages.map((s) => s.id || s.tempId)}
                strategy={horizontalListSortingStrategy}
              >
                {pipelineStages.map((stage) => (
                  <PipelineStageForm
                    key={stage.id || stage.tempId}
                    id={stage.id || stage.tempId}
                    stage={stage}
                    onChange={(field, value) =>
                      onStageChange(stage.id || stage.tempId, field, value)
                    }
                    onDeleteClick={() => {
                      if (leads?.filter((l) => l.stage === stage.id)) {
                        showAlert('warning', 'Only empty stages can be deleted. Please move any leads to an alternative stage and then try again.');
                        return;
                      }
                      setSelectedStage(stage);
                      toggleDeleteStageModal(true);
                    }}
                    isLost
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleLeadDragEnd}
              onDragStart={({ active }) => setActiveLeadId(active.id)}
              onDragCancel={() => setActiveLeadId(null)}
            >
              {pipelineStages.map((stage, index) => (
                <SortableContext
                  key={stage.id}
                  items={filteredLeads.filter((l) => l.stage === stage.id).map((l) => l.id)}
                  id={stage.id}
                  strategy={rectSortingStrategy}
                >
                  <PipelineStage
                    isLast={index === pipelineStages.length - 1}
                    stage={stage}
                    leads={orderBy(filteredLeads?.filter((l) => l.stage === stage.id), 'orderNumber')}
                    surgeons={surgeons}
                    labels={labels}
                    onLeadClick={onLeadClick}
                    activeLeadId={activeLeadId}
                    tenantColor={currentTenant.colorPrimary}
                    surgeonRanking={currentTenant.surgeonRanking}
                  />
                </SortableContext>
              ))}
              <DragOverlay>
                {activeLeadId ? (
                  <div className="lead-card--overlay">
                    <LeadCard
                      isOverlay
                      lead={leads.find((l) => l.id === activeLeadId)}
                      surgeons={surgeons}
                      labels={labels}
                      tenantColor={currentTenant.colorPrimary}
                      surgeonRanking={currentTenant.surgeonRanking}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {editMode && (
            <div className='sales-page__new-stage'>
              <Button
                text='Add New Stage'
                onClick={onAddStageClick}
                type='secondary'
              />
            </div>
          )}
        </div>
        <div className="scroll-shadow-left" />
        <div className="scroll-shadow-right" />
      </div>

      <ConfirmationModal
        open={deleteStageModalOpen}
        onClose={() => toggleDeleteStageModal(false)}
        onSubmit={onDeleteStage}
        title='Delete Stage'
        text='You are about to delete this stage. Any related leads will be removed forever.'
        submitText='Delete'
        loading={deleteStageLoading}
      />

      <SalesFiltersModal
        open={filtersModalOpen}
        onClose={() => toggleFiltersModal(false)}
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        users={users}
        surgeons={surgeons}
        labels={labels}
        active={!!activeFilterCount}
      />
    </div>
  );
};

export default SalesLeadsPage;
